import { apiClient } from "@/lib/apiClient";
import type {
  AddUserGroupMembersPayload,
  CreateUserGroupPayload,
  UserGroupDetail,
  UserGroupDetailApi,
  UserGroupDetailResponseApi,
  UserGroupListItem,
  UserGroupListItemApi,
  UserGroupsListParams,
  UserGroupsListResponseApi,
} from "../types/user-groups.types";

const ENDPOINT = "/api/admin/user-groups";

function normalizeListItem(item: UserGroupListItemApi): UserGroupListItem {
  return {
    id: item.id,
    name: item.name ?? "—",
    description: item.description ?? null,
    memberCount: item.memberCount ?? item.membersCount ?? item.userCount ?? 0,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
  };
}

function normalizeDetail(item: UserGroupDetailApi): UserGroupDetail {
  return {
    ...normalizeListItem(item),
    members: (item.members ?? []).map((member) => ({
      id: member.user?.id ?? member.userID ?? member.id,
      firstName: member.user?.firstName ?? member.firstName ?? null,
      lastName: member.user?.lastName ?? member.lastName ?? null,
      email: member.user?.email ?? member.email ?? null,
      identityNumber: member.identityNumber ?? null,
      ldapUsername: member.ldapUsername ?? null,
      title: member.title ?? null,
      department:
        !member.department || typeof member.department === "string"
          ? null
          : member.department,
    })),
  };
}

function emptyDetail(id: string, name = "—"): UserGroupDetail {
  return {
    id,
    name,
    description: null,
    memberCount: 0,
    createdAt: undefined,
    updatedAt: undefined,
    members: [],
  };
}

export const userGroupsApi = {
  async getList(
    params?: UserGroupsListParams,
  ): Promise<{ data: UserGroupListItem[]; total: number }> {
    const { data } = await apiClient.get<UserGroupsListResponseApi>(ENDPOINT, {
      params,
    });
    const items = Array.isArray(data?.data)
      ? data.data.map(normalizeListItem)
      : [];
    return {
      data: items,
      total: data?.count ?? data?.total ?? items.length,
    };
  },

  async getById(id: string): Promise<UserGroupDetail> {
    const { data } = await apiClient.get<UserGroupDetailResponseApi>(
      `${ENDPOINT}/${id}`,
    );
    return data?.data ? normalizeDetail(data.data) : emptyDetail(id);
  },

  async create(payload: CreateUserGroupPayload): Promise<UserGroupDetail> {
    const { data } = await apiClient.post<UserGroupDetailResponseApi>(
      ENDPOINT,
      payload,
    );
    return data?.data
      ? normalizeDetail(data.data)
      : emptyDetail("", payload.name);
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`${ENDPOINT}/${id}`);
  },

  async addMembers(
    id: string,
    payload: AddUserGroupMembersPayload,
  ): Promise<UserGroupDetail> {
    const { data } = await apiClient.post<UserGroupDetailResponseApi>(
      `${ENDPOINT}/${id}/members`,
      payload,
    );
    return data?.data ? normalizeDetail(data.data) : emptyDetail(id);
  },

  async removeMember(id: string, userId: string): Promise<UserGroupDetail> {
    const { data } = await apiClient.delete<UserGroupDetailResponseApi>(
      `${ENDPOINT}/${id}/members/${userId}`,
    );
    return data?.data ? normalizeDetail(data.data) : emptyDetail(id);
  },
};
